<template>
  <div class="bg-base-200 mb-6">
    <div class="flex items-center justify-between p-4">
      <h2 class="text-2xl font-bold">Danh sách phiên bản</h2>
      <ReconVersionCreateModal />
    </div>
    <div class="flex gap-4 p-4 pt-0">
      <label class="input input-bordered w-full max-w-xs">
        <i class="fa-solid fa-magnifying-glass text-base-content/80"></i>
        <input
          type="text"
          aria-label="Tìm kiếm theo tên hoặc mô tả"
          placeholder="Tìm kiếm theo tên hoặc mô tả..."
          :value="keyword"
          @input="handleSearch"
        />
      </label>

      <select
        :value="selectedType"
        class="select select-bordered w-full max-w-xs"
        @change="handleTypeChange"
      >
        <option value="">Tất cả loại</option>
        <option value="recon">Recon</option>
        <option value="password_detection">Password Detection</option>
        <option value="bruteforce">Bruteforce</option>
        <option value="spread_lan">Spread LAN</option>
      </select>
    </div>
    <div class="p-4 pt-0">
      <div v-if="isLoading" class="text-center">Đang tải...</div>
      <div v-else-if="error" class="text-error text-center">
        Lỗi: {{ error.message }}
      </div>
      <template v-else-if="reconData?.data?.length">
        <PaginationTemplate class="mb-3" :total_pages="total_pages" />
        <div class="overflow-x-auto">
          <table class="table-zebra bg-base-100 table w-full">
            <thead>
              <tr>
                <th>Tên</th>
                <th>Loại</th>
                <th>Mô tả</th>
                <th>Ngày tạo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in reconData.data" :key="item.id">
                <td>{{ item.version }}</td>
                <td>{{ item.type }}</td>
                <td>{{ item.description }}</td>
                <td :title="dayjs(item.created).format('YYYY-MM-DD HH:mm:ss')">
                  {{ dayjs(item.created).fromNow() }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <div v-else class="text-center">Không có bản ghi nào.</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { number_from_query, str_from_query } from "@/api";
import { axios_instance } from "@/api/api";
import PaginationTemplate from "@/components/PaginationTemplate.vue";
import { router } from "@/router/router";
import { useQuery } from "@tanstack/vue-query";
import dayjs from "dayjs";
import { computed, ref } from "vue";
import type { version } from "./index";
import ReconVersionCreateModal from "./ReconVersionCreateModal.vue";

const keyword = computed(() => str_from_query("keyword") || "");
const selectedType = computed(() => str_from_query("type") || "");
const debounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);

const handleTypeChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  const newType = target.value;

  router.push({
    query: {
      ...router.currentRoute.value.query,
      type: newType || undefined,
      page: undefined,
    },
  });
};

const handleSearch = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newKeyword = target.value;

  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }

  debounceTimer.value = setTimeout(() => {
    router.push({
      query: {
        ...router.currentRoute.value.query,
        keyword: newKeyword || undefined,
        page: undefined,
      },
    });
  }, 500);
};

const page = computed(() => number_from_query("page", 1));
const size = computed(() => number_from_query("size", 10));
const queryParams = computed(() => ({
  keyword: keyword.value,
  type: selectedType.value,
  page: page.value,
  size: size.value,
}));

const fetchReconVersions = async () => {
  const types = queryParams.value.type
    ? [queryParams.value.type]
    : ["recon", "password_detection", "bruteforce", "spread_lan"];

  const data = JSON.stringify({
    keyword: queryParams.value.keyword,
    page: queryParams.value.page,
    size: queryParams.value.size,
    type: types,
  });

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: "/api/v1/view/version/pagination",
    data: data,
  };

  const response = await axios_instance.request<{
    data: version[] | null;
    total_filter: number;
  }>(config);
  return response.data;
};

const {
  data: reconData,
  isLoading,
  error,
} = useQuery({
  queryKey: ["recon", queryParams],
  queryFn: fetchReconVersions,
});

const total_pages = computed(() =>
  Math.ceil((reconData.value?.total_filter || 0) / size.value),
);
</script>
